import { escapeHtml } from 'src/app/helpers/support-code.helpers';
import type { CheckinRowVm, JanelaVm } from './checkin-row.vm';

/**
 * Corpo dos diálogos da tela de Check-ins, como funções puras.
 *
 * Mora fora do componente pelo mesmo motivo do viewmodel: os três diálogos que
 * listam janelas divergiram entre si quando cada um montava o próprio HTML —
 * um usava formato longo, outro curto, e os dois despejavam as catorze linhas.
 *
 * Tudo que vem de fora passa por `escapeHtml`: nome de participante é campo
 * livre e o `html` do Swall não escapa nada sozinho.
 */

/** Uma linha da dobra de janelas: período e o que aconteceu nela. */
function linhaJanela(j: JanelaVm): string {
  const feito = j.checkin
    ? `<span style="color:#0A7A35;font-weight:600">usada ${escapeHtml(j.checkin.horario)}</span>`
    : `<span style="color:#8A8A8A">${escapeHtml(j.statusLabel)}</span>`;
  const destaque = j.status === 'ativa' ? 'background:#F3F1FF;' : '';
  return (
    `<div style="display:flex;justify-content:space-between;gap:10px;` +
    `padding:6px 9px;border-radius:8px;${destaque}">` +
    `<span>${escapeHtml(j.periodoCurto)}</span>${feito}</div>`
  );
}

/**
 * Resumo em UMA linha, com a lista atrás de um `<details>`.
 *
 * Um lote de janela diária tem catorze; despejadas, elas ocupam o diálogo
 * inteiro e empurram os botões para fora da dobra no celular — e nenhuma delas
 * responde a pergunta de quem está decidindo. `<details>` porque é nativo:
 * dentro de um Swall não há Angular para ligar um clique.
 */
export function blocoJanelas(janelas: JanelaVm[]): string {
  if (!janelas.length) return '';

  const usadas = janelas.filter((j) => j.checkin).length;
  const partes = [`${janelas.length} janelas`];
  if (usadas) partes.push(`${usadas} usada${usadas > 1 ? 's' : ''}`);

  return (
    `<details style="margin-top:.85rem;border-top:1px solid #E6E6EC;padding-top:.7rem">` +
    `<summary style="cursor:pointer;font-size:.8rem;color:#666;list-style:none;` +
    `display:flex;justify-content:space-between;gap:10px">` +
    `<span>${escapeHtml(partes.join(' · '))}</span>` +
    `<span style="color:#604AFF;font-weight:700">Ver janelas</span></summary>` +
    `<div style="display:flex;flex-direction:column;gap:2px;margin-top:.5rem;font-size:.78rem">` +
    janelas.map(linhaJanela).join('') +
    `</div></details>`
  );
}

/** Bloco de identificação: quem é a pessoa, sem virar formulário. */
function blocoPessoa(nome: string, linhas: string[]): string {
  const secundarias = linhas.filter((v) => v && v !== '--').map(escapeHtml).join(' · ');
  return (
    `<div style="padding:.7rem .8rem;border-radius:10px;background:#F7F7FA;text-align:left">` +
    `<div style="font-weight:700;color:#292929">${escapeHtml(nome)}</div>` +
    (secundarias ? `<div style="font-size:.8rem;color:#666;margin-top:2px">${secundarias}</div>` : '') +
    `</div>`
  );
}

/**
 * Por que este check-in é forçado, em uma frase.
 *
 * É o que o diálogo antigo não dizia: ele mostrava "Fora da janela" com duas
 * janelas ABERTAS na lista, sem contar que as duas já tinham sido usadas — e
 * assim parecia contradizer a própria lista.
 */
export function motivoDeForcar(row: CheckinRowVm): string {
  const abertas = row.janelas.filter((j) => j.status === 'ativa');
  const abertasUsadas = abertas.filter((j) => j.checkin).length;

  if (abertas.length && abertasUsadas === abertas.length) {
    return abertas.length === 1
      ? 'A janela aberta agora já foi usada.'
      : `As ${abertas.length} janelas abertas agora já foram usadas.`;
  }

  const proxima = row.janelas.find((j) => j.status === 'futura');
  if (proxima) return `Nenhuma janela aberta agora. A próxima é ${proxima.periodoCurto}.`;
  return 'Nenhuma janela de entrada aberta agora.';
}

export interface OpcoesDialogo {
  title: string;
  html: string;
  confirmButtonText: string;
  showCancelButton?: boolean;
  cancelButtonText?: string;
  reverseButtons?: boolean;
  customClass?: { confirmButton?: string };
}

/** Confirmação de check-in — normal ou forçado. */
export function corpoConfirmarCheckin(row: CheckinRowVm): OpcoesDialogo {
  const pessoa = blocoPessoa(row.nome, [row.lote, row.documento]);

  if (!row.forcaraCheckin) {
    return {
      title: 'Confirmar check-in',
      html: `<div style="text-align:left">${pessoa}</div>`,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Registrar check-in',
      reverseButtons: true,
    };
  }

  return {
    title: 'Forçar check-in fora da janela',
    html:
      `<div style="text-align:left">${pessoa}` +
      `<div style="margin-top:.7rem;padding:.7rem .8rem;border-radius:10px;` +
      `background:#FFF9EB;border:1px solid #F2D89A">` +
      `<div style="font-weight:700;color:#6B4E00;font-size:.88rem">${escapeHtml(motivoDeForcar(row))}</div>` +
      `</div>` +
      blocoJanelas(row.janelas) +
      `</div>`,
    showCancelButton: true,
    cancelButtonText: 'Cancelar',
    confirmButtonText: 'Registrar mesmo assim',
    reverseButtons: true,
    // Forçar contorna uma regra do evento; não pode pesar igual ao check-in
    // comum, que segue no roxo da marca.
    customClass: { confirmButton: 'swal-botao-destrutivo' },
  };
}

export interface DadosJaRealizado {
  nome: string;
  nomeLote: string;
  dataCheckin: string;
  operador: string;
  email: string;
  cpf: string;
}

/**
 * 409 de check-in já realizado.
 *
 * A resposta que encerra a discussão na porta — quando entrou e por quem — vem
 * primeiro e grande. Antes era a terceira linha de um bloco de cinco rótulos,
 * no mesmo corpo do resto.
 */
export function corpoJaRealizado(
  d: DadosJaRealizado | null,
  janelas: JanelaVm[] = [],
): OpcoesDialogo {
  // Piso: sem NENHUM dos três, o corpo rico renderizaria um cabeçalho vazio.
  // Um campo faltando, porém, não esconde os outros — ver
  // `dadosJaRealizadoParcial`.
  if (!d || (d.nome === '--' && d.operador === '--' && d.dataCheckin === '--')) {
    return {
      title: 'Check-in já realizado',
      html: '<p style="margin:0">Este ingresso já possui check-in.</p>',
      confirmButtonText: 'Entendi',
    };
  }

  const quando = d.dataCheckin && d.dataCheckin !== '--' ? d.dataCheckin : null;
  const porQuem = d.operador && d.operador !== '--' ? `por ${d.operador}` : '';

  return {
    title: 'Check-in já realizado',
    html:
      `<div style="text-align:left">` +
      `<div style="padding:.2rem 0 .8rem">` +
      // Âmbar escuro, o mesmo de "janela encerrada" na tabela. Em verde esta
      // linha era a assinatura visual do sucesso.
      `<div style="font-size:.78rem;font-weight:700;color:#946A00;letter-spacing:.02em">JÁ ENTROU</div>` +
      (quando
        ? `<div style="font-size:1.05rem;font-weight:700;color:#292929;margin-top:2px">${escapeHtml(quando)}</div>`
        : '') +
      (porQuem ? `<div style="font-size:.85rem;color:#666">${escapeHtml(porQuem)}</div>` : '') +
      `</div>` +
      blocoPessoa(d.nome, [d.nomeLote, d.cpf, d.email]) +
      blocoJanelas(janelas) +
      `</div>`,
    confirmButtonText: 'Entendi',
  };
}
